import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../contexts/UserContext";
import { useNotifications } from "../contexts/NotificationContext";
import apiService from "../services/api";
import StreakDisplay from "./StreakDisplay";
import BrandLogo from "./BrandLogo";
import "../styles/Navbar.css";

export default function Navbar({ onToggleSidebar }) {
  const [profileOpen, setProfileOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [stats, setStats] = useState({ streak: 0, points: 0, hasCompletedToday: false });
  const [achievementCount, setAchievementCount] = useState(0);
  const [pendingFriendActionId, setPendingFriendActionId] = useState(null);
  const profileRef = useRef(null);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();
  const { user, logout } = useUser();
  const { notifications, unreadCount, markAllAsRead, clearAllNotifications, loadNotifications, removeNotification } = useNotifications();

  const handleFriendRequestAction = async (event, notification, action) => {
    event.stopPropagation();
    const requestId = notification.data?.requestId;
    if (!requestId || pendingFriendActionId === requestId) return;

    try {
      setPendingFriendActionId(requestId);
      if (action === "accept") {
        await apiService.acceptFriendRequest(requestId, notification.id);
      } else {
        await apiService.declineFriendRequest(requestId, notification.id);
      }
      removeNotification(notification.id);
      // Notification is deleted on the backend, so we don't need to mark it as read
      await loadNotifications();
      window.dispatchEvent(new CustomEvent("friendsRefresh"));
    } catch (error) {
      console.error(`Failed to ${action} friend request:`, error);
    } finally {
      setPendingFriendActionId(null);
    }
  };

  // Load user stats and achievements
  useEffect(() => {
    const loadStats = async () => {
      if (user && apiService.getToken()) {
        try {
          const [statsResponse, achievementsResponse] = await Promise.all([
            apiService.getActivityStats(),
            apiService.getEarnedAchievements()
          ]);
          setStats(statsResponse.stats);
          setAchievementCount(achievementsResponse.achievements.length);
        } catch (error) {
          console.error('Failed to load navbar stats:', error);
        }
      }
    };
    
    loadStats();
  }, [user]);

  // Load notifications when user is available
  useEffect(() => {
    if (user && apiService.getToken()) {
      loadNotifications();
    }
  }, [user, loadNotifications]);

  // Listen for custom events to refresh stats
  useEffect(() => {
    const handleStatsRefresh = async () => {
      if (user && apiService.getToken()) {
        try {
          const [statsResponse, achievementsResponse] = await Promise.all([
            apiService.getActivityStats(),
            apiService.getEarnedAchievements()
          ]);
          setStats(statsResponse.stats);
          setAchievementCount(achievementsResponse.achievements.length);
        } catch (error) {
          console.error('Failed to refresh navbar stats:', error);
        }
      }
    };

    window.addEventListener('statsRefresh', handleStatsRefresh);
    return () => window.removeEventListener('statsRefresh', handleStatsRefresh);
  }, [user]);

  // Close menus when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target)) {
        setNotificationsOpen(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <header className="app-header" role="banner">
      <div className="header-left">
        <button
          className="burger-btn"
          onClick={() => onToggleSidebar && onToggleSidebar()}
          aria-label="Toggle menu"
        >
          <span className="burger-lines" aria-hidden>{'\u2630'}</span>
        </button>

        <div className="brand" onClick={() => navigate("/dashboard")} style={{ cursor: "pointer" }}>
          <BrandLogo size="small" />
          <div className="brand-copy">
            <div className="brand-title">PrepRight</div>
            <div className="brand-sub">Cook smarter, waste less</div>
          </div>
        </div>
      </div>

      <div className="header-right">
        <div 
          className="icon-btn icon-oval" 
          title="Achievements" 
          aria-hidden
          onClick={() => navigate('/achievements')}
          style={{ cursor: 'pointer' }}
        >
          <div className="achievement-header">
            <span className="icon-emoji">{'\uD83C\uDFC6'}</span>
            <span className="badge-number">{achievementCount}</span>
          </div>
          <div className="achievement-label">Total achievements</div>
        </div>

        <div className="navbar-streak-container">
          <StreakDisplay
            currentStreak={stats.streak}
            isCompletedToday={stats.hasCompletedToday} // Use the actual today completion status
            onStreakClick={() => {}} // No navigation
            previousStreak={stats.previousStreak}
            isActive={stats.isActive}
          />
        </div>

        <div className="notification-wrap" ref={notificationsRef}>
          <button 
            className="icon-btn notification-btn" 
            title="Notifications" 
            aria-label="Notifications"
            onClick={() => setNotificationsOpen(!notificationsOpen)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount}</span>
            )}
          </button>

          {notificationsOpen && (
            <div className="notifications-modal-overlay navbar-notifications-overlay">
              <div className="notifications-modal navbar-notifications-modal">
                <div className="notifications-header">
                  <h2>Notifications</h2>
                  <div className="notifications-header-actions">
                    {notifications.length > 0 && unreadCount > 0 && (
                      <button 
                        className="mark-all-read-btn"
                        onClick={markAllAsRead}
                      >
                        Mark all read
                      </button>
                    )}
                    {notifications.length > 0 && (
                      <button 
                        className="clear-all-notifications-btn"
                        onClick={clearAllNotifications}
                      >
                        Clear all
                      </button>
                    )}
                    <button 
                      className="close-notifications-btn"
                      onClick={() => setNotificationsOpen(false)}
                    >
                      &times;
                    </button>
                  </div>
                </div>
                <div className="notifications-content">
                  {notifications.length > 0 ? (
                    <div className="regular-notifications-section">
                      {notifications.map((notification) => {
                        const requestId = notification.data?.requestId;
                        const isFriendActionPending = requestId && pendingFriendActionId === requestId;

                        return (
                        <div key={notification.id} className="navbar-notification-item">
                          <div className="navbar-notification-icon">
                            {notification.type === 'achievement' && '\uD83C\uDFC6'}
                            {notification.type === 'level_up' && '\u2B50'}
                            {notification.type === 'like' && '\u2764\uFE0F'}
                            {notification.type === 'comment' && '\uD83D\uDCAC'}
                            {notification.type === 'system' && '\uD83D\uDD14'}
                            {notification.type === 'friend_request' && '\uD83D\uDC65'}
                          </div>
                          <div className="navbar-notification-body">
                            <div className="navbar-notification-title">{notification.title}</div>
                            <div className="navbar-notification-message">{notification.message}</div>
                            <div className="navbar-notification-time">
                              {new Date(notification.timestamp || notification.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          {notification.type === 'friend_request' && requestId && (
                              <div className="navbar-notification-actions">
                                <button
                                  type="button"
                                  className="accept-friend-btn"
                                  disabled={isFriendActionPending}
                                  onClick={(event) => handleFriendRequestAction(event, notification, "accept")}
                                >
                                  Accept
                                </button>
                                <button
                                  type="button"
                                  className="decline-friend-btn"
                                  disabled={isFriendActionPending}
                                  onClick={(event) => handleFriendRequestAction(event, notification, "decline")}
                                >
                                  Decline
                                </button>
                              </div>
                          )}
                          {!notification.isRead && <div className="unread-indicator"></div>}
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="no-notifications">
                      <div className="no-notifications-icon">{'\uD83D\uDD14'}</div>
                      <p>No notifications yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>


        <div className="profile-wrap" ref={profileRef}>
          {!user ? (
            <div className="guest-nav-actions">
              <button type="button" className="guest-login-btn" onClick={() => navigate("/login")}>
                Log in
              </button>
              <button type="button" className="guest-register-btn" onClick={() => navigate("/register")}>
                Register
              </button>
            </div>
          ) : (
            <>
          <button
            className="profile-btn"
            aria-haspopup="true"
            aria-expanded={profileOpen}
            onClick={() => setProfileOpen((s) => !s)}
            title="Account"
          >
            {user?.profilePicture ? (
              <img 
                src={apiService.getImageUrl(user.profilePicture)} 
                alt="Profile" 
                className="profile-picture"
              />
            ) : (
              <span className="profile-initial">{user?.name?.charAt(0) || "U"}</span>
            )}
          </button>

          {profileOpen && (
            <div className="profile-menu" role="menu" aria-label="Account menu">
              <button 
                role="menuitem" 
                className="profile-menu-item"
                onClick={() => {
                  navigate("/profile");
                  setProfileOpen(false);
                }}
              >
                Profile
              </button>
              <button 
                role="menuitem" 
                className="profile-menu-item"
                onClick={() => {
                  navigate("/settings");
                  setProfileOpen(false);
                }}
              >
                Settings
              </button>
              <button 
                role="menuitem" 
                className="profile-menu-item"
                onClick={() => {
                  logout();
                  navigate("/login");
                  setProfileOpen(false);
                }}
              >
                Logout
              </button>
            </div>
          )}
            </>
          )}
        </div>
      </div>
    </header>
  );
}

